/**
 * CelebrationPopup component
 * Displays a celebratory overlay with confetti animation when a question is answered correctly
 * Shows tiered congratulatory messages based on bonus earned
 * Auto-dismisses after 10 seconds or can be dismissed immediately via Continue button
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import pebblesIcon from '@/assets/pebbles-icon.png';

export interface CelebrationPopupProps {
  /** Total Pebbles earned for this question (2, 5, or 10) */
  totalAwarded: number;
  /** Bonus Pebbles (0, 3, or 8) */
  bonusAwarded: number;
  /** Callback when popup is dismissed (by timer or user tap) */
  onDismiss: () => void;
  /** Controls visibility of the popup */
  isVisible: boolean;
}

// Confetti colors using pebbles and complementary warm tones
const CONFETTI_COLORS = [
  '#d49a54', // pebbles-400
  '#c4862f', // pebbles-500
  '#a36e25', // pebbles-600
  '#fbbf24', // amber-400
  '#f59e0b', // amber-500
  '#60a5fa', // blue-400
  '#4ade80', // green-400
  '#f472b6', // pink-400
];

// Message variants for each tier (random selection for variety)
const MESSAGE_VARIANTS = {
  tier0: [
    'Nice effort! You earned {total} Pebbles.',
    'Good try! You earned {total} Pebbles.',
    'Keep going! You earned {total} Pebbles.',
  ],
  tier3: [
    'Well done! You earned {total} Pebbles!',
    'Great work! You earned {total} Pebbles!',
    'Nice job! You earned {total} Pebbles!',
  ],
  tier8: [
    'First try! Amazing! You earned {total} Pebbles!',
    'Perfect! Nailed it! You earned {total} Pebbles!',
    'Brilliant! First attempt! You earned {total} Pebbles!',
  ],
};

/**
 * Returns the congratulatory message based on bonus tier
 */
function getMessageForTier(
  bonusAwarded: number,
  totalAwarded: number
): { headline: string; showBonus: boolean } {
  let variants: string[];
  let showBonus = false;

  if (bonusAwarded === 8) {
    variants = MESSAGE_VARIANTS.tier8;
    showBonus = true;
  } else if (bonusAwarded === 3) {
    variants = MESSAGE_VARIANTS.tier3;
    showBonus = true;
  } else {
    variants = MESSAGE_VARIANTS.tier0;
    showBonus = false;
  }

  // Pick a random variant
  const randomIndex = Math.floor(Math.random() * variants.length);
  const headline = variants[randomIndex].replace('{total}', totalAwarded.toString());

  return { headline, showBonus };
}

/**
 * CelebrationPopup - Displays celebration overlay with confetti
 * Follows UI/UX framework specifications exactly
 */
export function CelebrationPopup({
  totalAwarded,
  bonusAwarded,
  onDismiss,
  isVisible,
}: CelebrationPopupProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check for prefers-reduced-motion preference
  useEffect(() => {
    if (!window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!mediaQuery) {
      return;
    }

    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Auto-dismiss timer
  useEffect(() => {
    if (isVisible) {
      timerRef.current = setTimeout(() => {
        onDismiss();
      }, 10000);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isVisible, onDismiss]);

  // Manual dismiss handler
  const handleDismiss = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    onDismiss();
  }, [onDismiss]);

  // Generate confetti particles (memoized for performance)
  const particles = useMemo(() => {
    if (prefersReducedMotion) {
      return [];
    }

    return Array.from({ length: 35 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      animationDelay: `${Math.random() * 0.5}s`,
      animationDuration: `${1.5 + Math.random() * 1}s`,
      backgroundColor: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      size: `${6 + Math.random() * 6}px`,
    }));
  }, [prefersReducedMotion]);

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  const { headline, showBonus } = getMessageForTier(bonusAwarded, totalAwarded);

  return (
    <>
      {/* CSS keyframes for confetti animation */}
      <style>{`
        @keyframes confettiFall {
          0% {
            transform: translateY(-20px) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(calc(100vh)) rotate(720deg);
            opacity: 0;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .confetti-particle {
            animation: none !important;
            opacity: 0;
          }
        }
      `}</style>

      {/* Overlay */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
        onClick={handleDismiss}
        role="dialog"
        aria-modal="true"
        aria-label={headline}
      >
        {/* Confetti particles */}
        <div className="absolute inset-0 overflow-hidden" style={{ pointerEvents: 'none' }}>
          {particles.map((particle) => (
            <span
              key={particle.id}
              className="confetti-particle absolute"
              style={{
                left: particle.left,
                top: 0,
                width: particle.size,
                height: particle.size,
                backgroundColor: particle.backgroundColor,
                borderRadius: '2px',
                animation: `confettiFall ${particle.animationDuration} ${particle.animationDelay} ease-out forwards`,
                willChange: 'transform, opacity',
              }}
            />
          ))}
        </div>

        {/* Centered card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 text-center max-w-xs mx-4 relative">
          {/* Pebbles icon */}
          <img
            src={pebblesIcon}
            alt=""
            aria-hidden="true"
            className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3"
          />

          {/* Headline message */}
          <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{headline}</h2>

          {/* Total count (large and prominent) */}
          <div className="text-2xl md:text-3xl font-bold text-pebbles-600 mb-1">
            {totalAwarded} Pebbles
          </div>

          {/* Bonus line - only shown when bonusAwarded > 0 */}
          {showBonus && bonusAwarded > 0 && (
            <div className="text-sm md:text-base font-semibold text-pebbles-500">
              Bonus: +{bonusAwarded} Pebbles
            </div>
          )}

          {/* Dismiss button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleDismiss();
            }}
            className="mt-4 px-6 py-2 bg-pebbles-500 hover:bg-pebbles-600 text-white font-semibold rounded-lg transition-colors"
          >
            Continue
          </button>
        </div>
      </div>
    </>
  );
}
